import { api } from './api';
import type {
  Conversation,
  ConversationMessage,
  ConversationStreamEvent,
  ConversationOverrides,
  SourceDetail,
} from '../types/conversation.types';

interface ApiResponse<T> {
  success: boolean;
  code: number;
  message: string;
  data: T;
}

interface PaginatedResponse<T> {
  success?: boolean;
  message?: string;
  data: T[];
  // The backend returns counts under `pagination`; flat fields kept optional for safety.
  pagination?: { page: number; pageSize: number; totalItems: number; totalPages: number };
  total?: number;
  totalPages?: number;
}

// Same base the shared axios instance uses, so the stream hits the same server.
const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/core';

export interface ConversationCreateRequest {
  agentCode: string;
  appId?: string;           // project to create under; omit to use the active project
  title?: string | null;
  variables?: Record<string, unknown> | null;
  overrides?: ConversationOverrides | null;
  metadata?: Record<string, unknown> | null;
}

export interface ConversationUpdateRequest {
  title?: string | null;
  variables?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
}

// Query params (camelCase aliases, matching the FastAPI Query names).
export interface ConversationListParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  agentCode?: string;
  appId?: string;
  includeDeleted?: boolean;
}

export interface MessagePageParams {
  page?: number;
  pageSize?: number;
  sortOrder?: 'asc' | 'desc';
}

/** Turn one SSE `data:` block into a typed event (mirrors document-processing.service). */
const parseBlock = (block: string): ConversationStreamEvent | null => {
  const data = block
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).trim())
    .join('');
  if (!data) return null;
  try {
    return JSON.parse(data) as ConversationStreamEvent;
  } catch {
    return null;
  }
};

export interface StreamHandlers {
  onEvent: (event: ConversationStreamEvent) => void;
  signal?: AbortSignal;
}

export const conversationsService = {
  list: async (params?: ConversationListParams): Promise<PaginatedResponse<Conversation>> => {
    const response = await api.get<PaginatedResponse<Conversation>>('/api/v1/conversations', { params });
    return response.data;
  },

  getById: async (id: string): Promise<Conversation> => {
    const response = await api.get<ApiResponse<Conversation>>(`/api/v1/conversations/${id}`);
    return response.data.data;
  },

  create: async (data: ConversationCreateRequest): Promise<Conversation> => {
    const response = await api.post<ApiResponse<Conversation>>('/api/v1/conversations', data);
    return response.data.data;
  },

  update: async (id: string, data: ConversationUpdateRequest): Promise<Conversation> => {
    const response = await api.patch<ApiResponse<Conversation>>(`/api/v1/conversations/${id}`, data);
    return response.data.data;
  },

  softDelete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete<ApiResponse<{ message: string }>>(`/api/v1/conversations/${id}`);
    return response.data.data;
  },

  restore: async (id: string): Promise<Conversation> => {
    const response = await api.patch<ApiResponse<Conversation>>(`/api/v1/conversations/${id}/restore`);
    return response.data.data;
  },

  listMessages: async (id: string, params?: MessagePageParams): Promise<PaginatedResponse<ConversationMessage>> => {
    const response = await api.get<PaginatedResponse<ConversationMessage>>(
      `/api/v1/conversations/${id}/messages`, { params });
    return response.data;
  },

  // The passage text behind one cited source (fetched from the index on demand).
  getMessageSource: async (id: string, messageId: string, index: number): Promise<SourceDetail> => {
    const response = await api.get<ApiResponse<SourceDetail>>(
      `/api/v1/conversations/${id}/messages/${messageId}/sources/${index}`);
    return response.data.data;
  },

  // Non-streaming send: waits, then returns both messages.
  sendMessage: async (
    id: string,
    body: { content: string; variables?: Record<string, unknown> | null },
  ): Promise<{ userMessage: ConversationMessage; message: ConversationMessage }> => {
    const response = await api.post<ApiResponse<{ userMessage: ConversationMessage; message: ConversationMessage }>>(
      `/api/v1/conversations/${id}/messages`, body);
    return response.data.data;
  },

  /**
   * Streaming send. Emits start → [retrieval] → delta… → done | error through
   * onEvent, parsing the server-sent events off the POST response body. Uses fetch
   * (not axios/EventSource) because this is a POST with a Bearer token and a body.
   *
   * Resolves when the stream closes. Refusals before the stream opens (404/409/422)
   * are thrown as errors; a failure mid-stream arrives as an `error` event.
   */
  streamMessage: async (
    id: string,
    body: { content: string; variables?: Record<string, unknown> | null },
    handlers: StreamHandlers,
  ): Promise<void> => {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE}/api/v1/conversations/${id}/messages/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      signal: handlers.signal,
    });

    if (!response.ok || !response.body) {
      // Refused before the stream started: a normal JSON error envelope.
      let message = `The server refused the message (${response.status}).`;
      try {
        const b = await response.json();
        message = b?.message || b?.errors?.details || message;
      } catch { /* not JSON — keep the status message */ }
      throw new Error(message);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // Events are separated by a blank line; the last piece may be partial.
      const blocks = buffer.split('\n\n');
      buffer = blocks.pop() ?? '';
      for (const block of blocks) {
        const event = parseBlock(block);
        if (event) handlers.onEvent(event);
      }
    }
  },
};
